// Printable yard sign (QR to the main page), neighbor flyer (QR to
// /neighborhood), and a house's login card (handed over from the admin console
// through localStorage, so the code never appears in a URL or history).
import qrcode from "qrcode-generator";
import { drawLights } from "./site.ts";

// One SVG path for all dark squares: crisp at any print size.
function qrSvg(text: string): SVGSVGElement {
  const qr = qrcode(0, "M");
  qr.addData(text);
  qr.make();
  const n = qr.getModuleCount();
  const quiet = 4; // the blank border scanners need
  let d = "";
  for (let r = 0; r < n; r++) {
    for (let c = 0; c < n; c++) if (qr.isDark(r, c)) d += `M${c + quiet} ${r + quiet}h1v1h-1z`;
  }
  const ns = "http://www.w3.org/2000/svg";
  const svg = document.createElementNS(ns, "svg");
  svg.setAttribute("viewBox", `0 0 ${n + quiet * 2} ${n + quiet * 2}`);
  svg.setAttribute("role", "img");
  svg.setAttribute("aria-label", `QR code for ${text}`);
  svg.setAttribute("shape-rendering", "crispEdges");
  const bg = document.createElementNS(ns, "rect");
  bg.setAttribute("width", "100%");
  bg.setAttribute("height", "100%");
  bg.setAttribute("fill", "#fff");
  const path = document.createElementNS(ns, "path");
  path.setAttribute("d", d);
  path.setAttribute("fill", "#000");
  svg.append(bg, path);
  return svg;
}

// A login card waiting from the admin console: print just that. Cards left
// over (the signs page didn't open in time) are thrown away unshown.
function showLoginCard(): void {
  let card: { username: string; code: string; expiresAt: number; storedAt?: number } | null = null;
  try {
    card = JSON.parse(localStorage.getItem("cc-login-card") ?? "null");
    localStorage.removeItem("cc-login-card");
  } catch {
    card = null;
  }
  if (!card || !card.storedAt || Date.now() - card.storedAt > 2 * 60 * 1000) return;
  const sheet = document.querySelector<HTMLElement>('[data-sheet="card"]')!;
  sheet.hidden = false;
  sheet.querySelector("[data-card-user]")!.textContent = card.username;
  sheet.querySelector("[data-card-code]")!.textContent = card.code;
  sheet.querySelector("[data-card-expires]")!.textContent = new Date(card.expiresAt).toLocaleDateString();
  document.body.dataset.printing = "card";
  setTimeout(() => window.print(), 600);
}

async function siteUrl(): Promise<string> {
  try {
    const res = await fetch("/api/street");
    if (res.ok) {
      const street = (await res.json()) as { publicUrl?: string };
      if (street.publicUrl) return street.publicUrl.replace(/\/+$/, "");
    }
  } catch {
    // Development hub without a street: use this address.
  }
  return location.origin;
}

async function main(): Promise<void> {
  document.querySelectorAll(".lights-string").forEach(drawLights);
  const base = await siteUrl();
  for (const node of document.querySelectorAll<HTMLElement>("[data-site]")) node.textContent = base.replace(/^https?:\/\//, "");
  const target = (path: string | undefined) => (path ? `${base}/${path}` : base);
  for (const node of document.querySelectorAll<HTMLElement>("[data-qr]")) {
    node.replaceChildren(qrSvg(target(node.dataset.qr)));
  }
  for (const node of document.querySelectorAll<HTMLElement>("[data-url]")) {
    node.textContent = target(node.dataset.url).replace(/^https?:\/\//, "");
  }
  showLoginCard();
  // Small festive bulbs along the top of each printed sheet.
  const colors = ["#d32f2f", "#2e7d32", "#f9a825", "#1565c0"];
  for (const strip of document.querySelectorAll<HTMLElement>(".sheet-lights")) {
    const count = Number(strip.dataset.bulbs ?? 18);
    for (let i = 0; i < count; i++) {
      const bulb = document.createElement("span");
      bulb.style.background = colors[i % colors.length]!;
      strip.append(bulb);
    }
  }
  for (const button of document.querySelectorAll<HTMLButtonElement>("[data-print]")) {
    button.addEventListener("click", () => {
      document.body.dataset.printing = button.dataset.print;
      window.print();
    });
  }
  window.addEventListener("afterprint", () => delete document.body.dataset.printing);
  // ?print=yard (or yard-letter, flyer) picks one sheet, for making PDFs with
  // headless Chrome's --print-to-pdf.
  const only = new URLSearchParams(location.search).get("print");
  if (only && !document.body.dataset.printing) document.body.dataset.printing = only;
}

void main();
